
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchBusinesses } from "@/services/business";
import { regions } from "@/data/regions";

export function useBusinessFilter() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get URL parameters with proper defaults
  const initialSearchTerm = searchParams.get("search") || "";
  const initialCategory = searchParams.get("category") || undefined;
  const initialRegion = searchParams.get("region") || undefined;
  const initialCity = searchParams.get("city") || undefined;
  
  // Ensure page parameter is properly parsed as a number with a fallback to 1
  const pageParam = searchParams.get("page");
  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  
  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [activeCategory, setActiveCategory] = useState<string | undefined>(initialCategory);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [cityFilter, setCityFilter] = useState<string | undefined>(initialCity);
  const [regionFilter, setRegionFilter] = useState<string | undefined>(initialRegion);

  // Fetch businesses from Supabase
  const { data: businesses, isLoading, error } = useQuery({
    queryKey: ['businesses'],
    queryFn: fetchBusinesses,
  });
  
  const itemsPerPage = 9;
  
  // Update URL parameters when filters change
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    
    // Update search parameter
    if (searchTerm) {
      newParams.set("search", searchTerm);
    } else {
      newParams.delete("search");
    }
    
    // Update category parameter
    if (activeCategory) {
      newParams.set("category", activeCategory);
    } else {
      newParams.delete("category");
    }
    
    // Update region parameter
    if (regionFilter) {
      newParams.set("region", regionFilter);
    } else {
      newParams.delete("region");
    }
    
    // Update city parameter
    if (cityFilter) {
      newParams.set("city", cityFilter);
    } else {
      newParams.delete("city");
    }
    
    // Update page parameter
    // We update the page parameter here as well to ensure consistency
    if (currentPage > 1) {
      newParams.set("page", currentPage.toString());
    } else {
      newParams.delete("page");
    }
    
    setSearchParams(newParams);
  }, [searchTerm, activeCategory, regionFilter, cityFilter, currentPage, searchParams, setSearchParams]);
  
  // Filter businesses based on search term, category, region, and city
  const filteredBusinesses = businesses
    ? businesses.filter((business) => {
        const matchesSearch = searchTerm 
          ? business.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
            business.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            business.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
          : true;
          
        const matchesCategory = activeCategory 
          ? business.category === activeCategory 
          : true;
          
        const matchesCity = cityFilter
          ? business.city.toLowerCase() === cityFilter.toLowerCase()
          : true;
          
        // Direct region matching using the new region field
        const matchesRegion = regionFilter
          ? business.region?.toLowerCase() === regionFilter.toLowerCase()
          : true;
          
        return matchesSearch && matchesCategory && matchesCity && matchesRegion;
      })
    : [];
  
  // Get unique list of cities for filtering
  const cities = businesses
    ? Array.from(new Set(businesses.map(business => business.city)))
    : [];
  
  // Get unique list of regions for filtering
  const availableRegions = businesses
    ? Array.from(new Set(businesses.map(business => business.region).filter(Boolean)))
    : [];
  
  // Pagination logic
  const totalPages = Math.ceil(filteredBusinesses.length / itemsPerPage);
  const paginatedBusinesses = filteredBusinesses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  // Handle functions - do not reset page for search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Do not reset page here, only update search term
  };
  
  // These functions reset to page 1 when changing filters
  const handleCategoryChange = (category: string | undefined) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };
  
  const handleCityFilter = (city: string | undefined) => {
    setCityFilter(city);
    setCurrentPage(1);
  };
  
  const handleRegionChange = (region: string | undefined) => {
    setRegionFilter(region);
    setCityFilter(undefined);
    setCurrentPage(1);
  };

  return {
    searchTerm,
    setSearchTerm,
    activeCategory,
    setActiveCategory: handleCategoryChange,
    currentPage,
    setCurrentPage,
    viewMode,
    setViewMode,
    cityFilter,
    setCityFilter: handleCityFilter,
    regionFilter,
    setRegionFilter: handleRegionChange,
    businesses,
    isLoading,
    error,
    filteredBusinesses,
    paginatedBusinesses,
    totalPages,
    cities,
    availableRegions,
    handleSearch,
    regions
  };
}
